
	import { Component, Vue, Prop, Watch, Emit } from "vue-property-decorator";
	import { namespace } from "vuex-class";
	import EventCategoriesTree from "./EventCategoriesTree.vue";
	import { EventCategory } from "@/store/event-category-setup/types";
	import VuePerfectScrollbar from "vue-perfect-scrollbar";

	const EventCategorySetup = namespace("eventCategorySetup");
	const GenericTableStore = namespace("GenericTable");

	@Component({
		components: {
			"event-categories-tree": EventCategoriesTree,
			VuePerfectScrollbar: VuePerfectScrollbar
		}
	})
	export default class EventOutcomeSelect extends Vue {
		$refs!: {
			areaTreeScrollBar: any;
			toggleBox: HTMLDivElement;
			searchBox: HTMLInputElement;
		};

		@EventCategorySetup.Action fetchEventCategoriesTree: () => void;
		@EventCategorySetup.State(state => state.eventCategoriesTree) eventCategoriesTree: EventCategory[];

        @EventCategorySetup.Mutation hideOutcome: (eventOutcome: EventCategory) => void;
	    @EventCategorySetup.Mutation showOutcome: (eventOutcome: EventCategory) => void;
		@EventCategorySetup.Mutation hideOutcomeSubTree: (eventOutcome: EventCategory) => void;
		@EventCategorySetup.Mutation showOutcomeSubTree: (eventOutcome: EventCategory) => void;

		@GenericTableStore.Getter getModalRow: any;

		// Prop required by generic table to pass selected value down 
		// to this component.
		@Prop()
		public value: number;

		public eventOutcomeSearch: string = "";        
		private filterFocus: boolean = false;
		private selectedOutcome: EventCategory = null;
		private selectedOutcomeInitialised: boolean = false;

		public async beforeMount() {
			await this.fetchEventCategoriesTree();
			this.$emit("component-loaded");
		}

		public async mounted() {
			// When opened in modal for adding an event outcome,
			// Selected outcome has already been initialised.
			if (this.getModalRow) {
				if(!this.getModalRow.hasOwnProperty("parentID") || this.getModalRow.parentID == null) {
					this.selectedOutcomeInitialised = true;
				}
			}

			if (this.value) {
				this.updateFromValue();
			}
		}

		/**
		 * Event outcome tree.
		 */
		public get tree(): EventCategory[] {
            return this.eventCategoriesTree;
		}

		@Watch("value")
		private valueChanged(value: string) {
			this.updateFromValue();
		}

		@Watch("selectedOutcome", { deep: true })
		private async parentIDChanged(newValue: any, oldValue: any) {
			// This runs once when mounted, only apply on subsequent changes.
			if(this.selectedOutcomeInitialised == false) {
				this.selectedOutcomeInitialised = true;
				return;
			}
			if(newValue == null) {
				return;
			}
			if(newValue.eventOutcomeID > 0 && this.selectedOutcomeInitialised) {
				// Send event to AreaSelect component to update the group ID.
				this.$root.$emit('updateGroupID', newValue.groupID, null);
			}
		}

		@Watch("eventOutcomeSearch")
		public oneventOutcomeSearchChange(value: string, oldValue: string) {
			// When search string changes, update dropdown with tree.
			if (value != oldValue) {
				this.setOutcomeTreeFilter(value);
			}
		}

		/**
		 * Method which updates the selected outcome.
		 */
		private async updateFromValue() {
			if (this.tree.length === 0) {
				// Catch tree being updated on beforeMounted
				// and update value when it has.
				const unwatch = this.$watch("tree", () => {
					if (this.tree.length > 0) {
						unwatch();
						this.updateFromValue();
					}
				});
				return;
			}
			if (this.value) {
				if(this.selectedOutcome != null && this.selectedOutcome.eventOutcomeID == this.value) {
					return;
				}
				// When value exists, use it to find the event outcome in the tree
				// then set it as selected.
				const outcome = await this.findCategoryIdInTree(this.value);
                if (outcome) {
                    this.selectedOutcome = outcome;
                }
			} else {
				this.selectedOutcome = null;
			}
		}

		/**
		 * Method to remove the currently selected outcome.
		 */
		public deselectEventOutcome(): void {
			this.selectedOutcome = null;
			this.$emit("input", null);
		}

		/**
		 * Method to select the eventoutcome and blur the searchbox.
		 */
		public treeEventOutcomeSelected(nodeOutcome: any) {
			this.selectEventOutcome(nodeOutcome.outcome);
			this.$refs.searchBox.blur();
		}

		/**
		 * Method to select the eventoutcome and reset the tree dropdown.
		 */
		public selectEventOutcome(eventOutcome: EventCategory): void {
			if (eventOutcome == null) {
				return
			}

			this.selectedOutcome = eventOutcome;
			this.eventOutcomeSearch = "";
			this.setOutcomeTreeFilter(this.eventOutcomeSearch);

            this.$emit("input", eventOutcome.eventOutcomeID);
		}

		/**
		 * Displays tree dropdown.
		 */
		private onSearchFocus(): void {
			this.filterFocus = true;
			this.setOutcomeTreeFilter(this.eventOutcomeSearch);
		}

		/**
		 * Resets the search query to an empty string and hides the dropdown after 200ms.
		 */
		private onSearchBlur(): void {
			setTimeout(() => {
				this.filterFocus = false;
				this.eventOutcomeSearch = "";
				this.setOutcomeTreeFilter(this.eventOutcomeSearch);
			}, 200);
		}

		/**
		 * Focuses the searchbox if it isn't already.
		 */
		private setSearchFocus() {
			if (!this.filterFocus) {
				this.$refs.searchBox.focus();
			}
		}
		
		/**
		 * Method to find outcome object in tree.
		 * @param eventOutcomeID ID of event outcome to find.
		 */
        private async findCategoryIdInTree(eventOutcomeID) {
            for(var i = 0; i < this.tree.length; ++i) {
                const searchResult = this.recursiveFind(this.tree[i], eventOutcomeID);
                if(searchResult) return searchResult;
            }
            return null;
		}

		/**
		 * Recursive method which does the searching for the event outcome object
		 * @param eventOutcome root of tree or sub tree to search.
		 * @param eventOutcomeID Id of event outcome to find.
		 */
		private recursiveFind(eventOutcome: EventCategory, eventOutcomeID: number) {
			if(eventOutcome.eventOutcomeID == eventOutcomeID) {
				return eventOutcome;
			}

			if(eventOutcome.childOutcomes && eventOutcome.childOutcomes.length > 0) {
				for(var i = 0; i < eventOutcome.childOutcomes.length; ++i) {
					const searchResult = this.recursiveFind(eventOutcome.childOutcomes[i], eventOutcomeID);
					if(searchResult) return searchResult;
				}
			}

			return null;
		}
		
		/**
		 * Method involved in altering the display of tree in the dropdown.
		 * @param outcomeSearch search string for eventoutcome.
		 */
		public setOutcomeTreeFilter(outcomeSearch: string) {
			const isEmptyOrSpaces = (str: string) => str == null || str.match(/^ *$/) != null;

			// If the search string is empty, just return the tree directly
			if (isEmptyOrSpaces(outcomeSearch)) {
				this.tree.forEach((node: any) => {
					this.recursiveShow(node);
				});
			} else {
				// Filter the tree, setting show for nodes that match filter
				this.tree.forEach((node: any) => {
					this.recursiveSearchApply(node, outcomeSearch.toLowerCase());
				});
			}
		}

		/**
		 * Recursive method to show the tree in its default form showing only top level outcomes.
		 * @param tree root of tree or sub tree.
		 */
		public recursiveShow(tree: EventCategory) {
			// Show all outcomes.
			this.showOutcome(tree);
			// However hide all sub outcomes until this is toggled true.
			this.hideOutcomeSubTree(tree);

			if (tree.childOutcomes && tree.childOutcomes.length > 0) {
				tree.childOutcomes.forEach((node: any) => {
					this.recursiveShow(node);
				});
			}
		}

		/**
		 * Recursive method to show the tree in response to searching for an outcome.
		 * @param tree root of tree or sub tree.
		 * @param searchTerm search term for event outcome.
		 */
		public recursiveSearchApply(tree: EventCategory, searchTerm: string): boolean {
			let thisHasTerm = tree.title!.toLowerCase().indexOf(searchTerm) > -1;

			if (tree.childOutcomes && tree.childOutcomes.length > 0) {
				tree.childOutcomes.forEach((node: any) => {
					const lowerInTree = this.recursiveSearchApply(node, searchTerm);
					if (lowerInTree) {
						thisHasTerm = true;
					}
				});
			}

			thisHasTerm ? this.showOutcome(tree) : this.hideOutcome(tree)
			this.showOutcomeSubTree(tree);

			return thisHasTerm;
		}
	}
